import { useEffect, useRef, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Clock } from "lucide-react";

const TIMEOUT_MS = 60 * 60 * 1000;   // 60 minutes — auto-logout
const WARNING_MS = 55 * 60 * 1000;   // 55 minutes — show warning
const COUNTDOWN_INTERVAL = 1000;
const ACTIVITY_EVENTS = ["mousedown", "keydown", "scroll", "touchstart", "mousemove"];
const THROTTLE_MS = 10000; // Only process activity every 10s to avoid perf hit

// CQ-001 FIX
// The previous implementation had `handleActivity` and `resetTimers` in the
// setup effect's dependency array. When the 55-min warning set `showWarning=true`,
// `handleActivity` (dep `[showWarning]`) got a new identity → the effect re-ran →
// cleanup cleared the logout timer and setup called `resetTimers()` again,
// clearing the warning and re-arming a full 55/60-min cycle. Logout never fired.
//
// Fix: keep live state in refs, callbacks stable, and arm timers only once
// per user change. State changes no longer cause timer re-arming.
export default function InactivityGuard({ children }) {
  const { user, logout } = useAuth();
  const [showWarning, setShowWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(300); // 5 min = 300s

  // Refs for timer state that must NOT trigger effect re-arming.
  const warningTimerRef = useRef(null);
  const logoutTimerRef = useRef(null);
  const countdownRef = useRef(null);
  const throttleRef = useRef(0);
  const showWarningRef = useRef(false);
  const logoutRef = useRef(logout);

  // Keep the ref mirror in sync with state so `handleActivity` can read the
  // current warning state without depending on it.
  useEffect(() => { showWarningRef.current = showWarning; }, [showWarning]);
  useEffect(() => { logoutRef.current = logout; }, [logout]);

  const clearAllTimers = useCallback(() => {
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);
    warningTimerRef.current = null;
    logoutTimerRef.current = null;
    countdownRef.current = null;
  }, []);

  const resetTimers = useCallback(() => {
    clearAllTimers();
    setShowWarning(false);

    // Arm warning at 55 min.
    warningTimerRef.current = setTimeout(() => {
      setShowWarning(true);
      setSecondsLeft(300);
      countdownRef.current = setInterval(() => {
        setSecondsLeft((prev) => {
          if (prev <= 1) {
            if (countdownRef.current) clearInterval(countdownRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, COUNTDOWN_INTERVAL);
    }, WARNING_MS);

    // Arm auto-logout at 60 min.
    logoutTimerRef.current = setTimeout(() => {
      if (countdownRef.current) clearInterval(countdownRef.current);
      setShowWarning(false);
      logoutRef.current?.();
    }, TIMEOUT_MS);
  }, [clearAllTimers]);

  const handleStayLoggedIn = useCallback(() => {
    resetTimers();
  }, [resetTimers]);

  // Setup effect — runs ONCE per user login/logout.
  // Listener callback reads live state via refs, so setState no longer causes
  // re-arm cycles.
  useEffect(() => {
    if (!user) return;

    const handleActivity = () => {
      const now = Date.now();
      if (now - throttleRef.current < THROTTLE_MS) return;
      throttleRef.current = now;
      // While the warning dialog is showing, activity must NOT auto-reset
      // (user must explicitly click "Stay Logged In"). Read from ref so this
      // decision does not depend on state identity.
      if (showWarningRef.current) return;
      resetTimers();
    };

    resetTimers();

    for (const event of ACTIVITY_EVENTS) {
      window.addEventListener(event, handleActivity, { passive: true });
    }

    return () => {
      for (const event of ACTIVITY_EVENTS) {
        window.removeEventListener(event, handleActivity);
      }
      clearAllTimers();
    };
  }, [user]);

  const formatTime = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

  if (!user) return children;

  return (
    <>
      {children}
      <AlertDialog open={showWarning} onOpenChange={() => {}}>
        <AlertDialogContent className="max-w-sm" data-testid="inactivity-warning">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-amber-700">
              <Clock className="w-5 h-5" />
              Session Expiring
            </AlertDialogTitle>
            <AlertDialogDescription className="text-slate-600">
              You've been inactive. Your session will expire in{" "}
              <span className="font-bold text-amber-700">{formatTime(secondsLeft)}</span>.
              <br />
              <span className="text-xs text-slate-400 mt-1 block">Any unsaved work will be preserved when you stay logged in.</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction
              onClick={handleStayLoggedIn}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="stay-logged-in-btn"
            >
              Stay Logged In
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
